/**
 * InferiorSourceModal.jsx — RC 1.44 / P0.4
 *
 * Hard-stop modal that fires when the backend returns:
 *
 *   HTTP 409 { detail: { code: 'INFERIOR_SOURCE'  | 'INFERIOR_VARIANT',
 *                        message, guidance: {…} } }
 *
 * Honest, four-option commercial-trust dialog. Verbatim copy
 * mirrors the directive:
 *
 *   • Stay with Source CV     — re-runs Analyse Fit on the
 *                               candidate's primary uploaded CV.
 *   • Choose stronger CV      — closes the modal so the
 *                               wizard's CV chooser stays in
 *                               focus.
 *   • Generate new CV         — routes to the wizard's CV-gen
 *                               step (Step 2 — `cvGen` panel).
 *   • Continue anyway         — re-runs the same call with
 *                               `force_override=true` so the
 *                               backend skips the inferiority
 *                               check (R2/R3) and the user
 *                               consciously consumes credits.
 *
 * Single source of truth — no inferiority logic on the client.
 * Code switching happens on the explicit `code` field returned
 * by the backend, not on string-matching the message.
 */
import { AlertTriangle, ArrowRight, FileText, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export default function InferiorSourceModal({
  open,
  code,                  // 'INFERIOR_SOURCE' | 'INFERIOR_VARIANT'
  guidance,              // { primary_cv_id, primary_cv_label, baseline_score, variant_score, ... }
  onClose,
  onStayWithSourceCv,    // re-run with primary uploaded_cv
  onChooseStrongerCv,    // dismiss; user picks another in Step 2
  onGenerateNewCv,       // route to Step 2 cvGen
  onContinueAnyway,      // re-run with force_override=true
  busyAction,            // 'stay' | 'continue' | null — show spinner on action btn
}) {
  const isVariant = code === 'INFERIOR_VARIANT';

  const headline = isVariant
    ? 'This generated variant is weaker than your baseline'
    : 'Selected CV is weaker than your Source CV';

  const description = isVariant
    ? `Your baseline (Source CV) scored ${guidance?.baseline_score ?? '—'}/100 on this job. The selected variant previously scored ${guidance?.variant_score ?? '—'}/100. Re-running Analyse Fit on the variant won't improve your competitiveness — and it will cost credits.`
    : `Analyse Fit is most accurate when run against your primary Source CV (${guidance?.primary_cv_label || 'your master CV'}). The CV you selected isn't your primary — running it would consume credits without improving your score.`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid="inferior-source-modal"
      >
        <DialogHeader>
          <div className="mx-auto mb-2 w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <DialogTitle
            className="text-center"
            data-testid="inferior-source-modal-title"
          >
            {headline}
          </DialogTitle>
          <DialogDescription
            className="text-center text-sm leading-relaxed"
            data-testid="inferior-source-modal-description"
          >
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-2 mt-2">
          <Button
            onClick={onStayWithSourceCv}
            disabled={busyAction === 'stay'}
            className="justify-between bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
            data-testid="inferior-source-modal-stay-source-btn"
          >
            <span className="inline-flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Stay with Source CV
            </span>
            <ArrowRight className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            onClick={onChooseStrongerCv}
            data-testid="inferior-source-modal-choose-btn"
            className="justify-between"
          >
            <span>Choose a stronger CV</span>
            <ArrowRight className="h-4 w-4 opacity-60" />
          </Button>

          <Button
            variant="outline"
            onClick={onGenerateNewCv}
            data-testid="inferior-source-modal-generate-btn"
            className="justify-between"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ndor-gold" />
              Generate a new CV
            </span>
            <ArrowRight className="h-4 w-4 opacity-60" />
          </Button>
        </div>

        <DialogFooter className="mt-3 flex-col-reverse sm:flex-row sm:justify-end gap-2 border-t pt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={onContinueAnyway}
            disabled={busyAction === 'continue'}
            className="text-xs text-ndor-ink-muted hover:text-ndor-ink"
            data-testid="inferior-source-modal-continue-anyway-btn"
          >
            Continue anyway (will use credits)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
